import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Trophy } from 'lucide-react';
import { useUserSummary, useGamificationHistory } from '@/hooks/useGamification';
import {
  HistoryRow, BadgeCard, EmptyStateTrophy,
} from './RewardsPage';
import styles from './RewardsPage.module.css';

/**
 * Header-right Lottie. Same lazy-load pattern as ProfilePage's
 * SheetIcon / CategoryIcon helpers — lottie-react ships in its
 * own chunk, the Lucide Trophy renders behind a Suspense
 * fallback during chunk load and as the static fallback if the
 * JSON ever fails to fetch. prefers-reduced-motion is respected
 * via a local matchMedia check; when reduced, we skip mounting
 * AnimatedIcon entirely and render the static Lucide glyph at
 * the same size — no animation, no chunk fetch.
 */
const AnimatedIcon = lazy(() => import('@/components/ui/AnimatedIcon'));

/** Size bumped 72 → 112 → 144 → 192 across rounds of founder
 *  feedback. 192 makes the trophy the dominant element of the
 *  header and pairs with the 'X badges earned' count stacked below it
 *  (see HeaderTrophyBlock). At 360px viewports the title block
 *  drops to ~124px wide and 'REWARD HISTORY' wraps to two lines —
 *  acceptable trade-off for the trophy hero treatment on
 *  desktop/tablet where the layout has room.
 *
 *  CSS container width below must stay in lockstep (AnimatedIcon
 *  writes the size to inline width/height on the SVG; a CSS-only
 *  container shrink would clip). */
const HEADER_ICON_SIZE_PX = 192;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function HeaderTrophyIcon(): React.JSX.Element {
  const reduced = usePrefersReducedMotion();
  const fallback = <Trophy size={HEADER_ICON_SIZE_PX} aria-hidden />;
  if (reduced) return fallback;
  return (
    <Suspense fallback={fallback}>
      <AnimatedIcon
        src="/icons/trophy.json"
        fallback={fallback}
        size={HEADER_ICON_SIZE_PX}
        playOnMount
        playOnTap
        loop
      />
    </Suspense>
  );
}

interface Props { onNavigate: (path: string) => void }

/**
 * Full reward-history page.
 *
 * Section order: Badges first, Reward history below. The Phase
 * 1.5 ship had history first; founder preference flipped it
 * post-ship — Badges are the milestone surface that rewards
 * a quick scan ("what did I earn lately?"); the ledger is for
 * deeper auditing. Heads-of-list goes to the glanceable
 * surface.
 *
 * Two queries on the page:
 *   - useUserSummary — page header + Badges section. Shared
 *     cache key with RewardsPage so navigating here from the
 *     View-All link is hot.
 *   - useGamificationHistory — cursor-paginated /gamification/
 *     history endpoint (Commit 1 of the rewards-pagination
 *     phase). Backend fetches 50 per page; this page slices
 *     client-side to show 5 by default and reveals more on
 *     Load More taps.
 *
 * Load More button two-mode behavior:
 *   1. visibleCount < entries.length — already loaded, just
 *      reveal the next 5 from local state. Cheap. No fetch.
 *   2. visibleCount === entries.length AND hasNextPage —
 *      fetch the next 50 from the server, then reveal the
 *      next 5 from the new total.
 * Hidden entirely when visibleCount === entries.length AND
 * !hasNextPage (end of feed reached).
 *
 * Why slice client-side rather than fetch 5 at a time:
 *   Server pagination at 5 per page would be 10× more
 *   round-trips for a driver scrolling deep history. The
 *   50-page-fetch + 5-reveal model batches the network cost
 *   and gives instant UI response on the first 9 reveals.
 *   visibleCount state survives React Query refetches (which
 *   is correct UX — refetching shouldn't yank scroll back to
 *   5).
 *
 * Redemption surfacing: REDEMPTION ledger rows (negative
 * pointsDelta written by the redeem flow at apps/api/src/
 * routes/gamification/redeem.ts) flow through the same
 * HistoryRow component as earn events. The component already
 * branches on pointsDelta sign — TrendingDown icon + warn-
 * tone pill for spends, TokenSpin + good-tone for earns. No
 * new code needed for redemption display; this page surfaces
 * them naturally via the unified ledger.
 *
 * Renders the SAME HistoryRow + BadgeCard + EmptyStateTrophy
 * components RewardsPage uses — those got export keywords in
 * the same commit so this page imports them without
 * duplication.
 */

const INITIAL_VISIBLE = 5;
const REVEAL_STEP = 5;

export function RewardsHistoryPage({ onNavigate }: Props) {
  const { t } = useTranslation('rewardsExtra');
  const summaryQ = useUserSummary();
  const historyQ = useGamificationHistory();
  const summary = summaryQ.data;
  // Client-side reveal counter. State persists across React
  // Query cache invalidations (window focus refetch, mutation
  // invalidation) so a driver who's revealed 30 entries doesn't
  // get yanked back to 5 when the page refetches in the
  // background. Reset only happens on component unmount.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  if (summaryQ.isLoading && !summary) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => onNavigate('/rewards')}>
          <ArrowLeft size={14} /> {t('back')}
        </button>
        <div className={styles.heroSkeleton} />
      </div>
    );
  }
  if (summaryQ.isError && !summary) {
    const msg = summaryQ.error instanceof Error
      ? summaryQ.error.message
      : t('errorLoadFailed');
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => onNavigate('/rewards')}>
          <ArrowLeft size={14} /> {t('back')}
        </button>
        <div className={styles.errorBox}>{msg}</div>
      </div>
    );
  }
  if (!summary) return null;

  // History from the paginated endpoint, flattened across pages.
  const entries = historyQ.data?.pages.flatMap((p) => p.entries) ?? [];
  const isLoadingFirstPage = historyQ.isLoading && !historyQ.data;
  const badges = summary.earnedBadges;

  // Load-More state machine. Three derived booleans:
  //   canRevealMore — already-loaded entries beyond visibleCount
  //   canFetchMore  — server has more pages
  //   showLoadMore  — either condition is true (button visible)
  const canRevealMore = visibleCount < entries.length;
  const canFetchMore = historyQ.hasNextPage;
  const showLoadMore = canRevealMore || canFetchMore;
  const displayedEntries = entries.slice(0, visibleCount);

  // Show "Load more failed" only when the most recent
  // fetchNextPage call errored AND we're not currently retrying.
  // The truthy check on `historyQ.error` covers both
  // first-page errors (handled by the early-return on
  // historyQ.isError above? no — that's summaryQ; historyQ
  // errors flow through here). Keep the inline message subtle
  // since the user can retry by tapping Load More again.
  const loadMoreError =
    historyQ.error
    && !historyQ.isFetchingNextPage
    && entries.length > 0;

  const handleLoadMore = async (): Promise<void> => {
    if (canRevealMore) {
      // Cheap path: just reveal more of what's already loaded.
      setVisibleCount((v) => Math.min(v + REVEAL_STEP, entries.length));
      return;
    }
    if (canFetchMore) {
      // Network path: fetch the next page, then reveal another
      // chunk of the larger pool. The fetch may fail (network /
      // server); React Query exposes the error via historyQ.error,
      // and the inline error message below the button surfaces
      // it. The button stays visible so the user can retry.
      try {
        await historyQ.fetchNextPage();
        setVisibleCount((v) => v + REVEAL_STEP);
      } catch {
        // Error already captured in historyQ.error; no-op here.
      }
    }
  };

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => onNavigate('/rewards')}>
        <ArrowLeft size={14} /> {t('back')}
      </button>

      {/* Page title — distinct from the parent /rewards which has
          its own hero. Here the "Reward history" framing IS the
          page identity. Header is a flex row: title block on the
          left (h1 + subtitle stacked), trophy Lottie on the
          right. The trophy shrinks on narrow viewports while the
          title block keeps a flex floor so the subtitle wraps
          rather than getting crushed. */}
      <header className={styles.historyPageHeader}>
        <div className={styles.historyPageTitleBlock}>
          <h1 className={styles.historyPageTitle}>{t('historyTitle')}</h1>
          <p className={styles.historyPageSub}>
            {t('historySubtitle')}
          </p>
        </div>
        <div className={styles.historyPageHeaderIcon}>
          <HeaderTrophyIcon />
          {/* Badge count moved here from the Badges section
              header (lower in the page) per founder feedback —
              centered beneath the trophy reads as 'this is what
              the trophy represents' instead of as a section
              meta label. The Badges section header now carries
              just the title. */}
          <span className={styles.historyPageHeaderIconCount}>
            {t('badgesEarned', { count: badges.length })}
          </span>
        </div>
      </header>

      {/* Badges — earned only, full grid. Promoted above
          Reward history (post-Phase-1.5 reorder) because
          Badges are the glanceable milestone surface; the
          ledger is the deeper-audit surface. Heads-of-list
          goes to the quicker scan. */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('badgesTitle')}</h2>
          {/* Earned count moved up to the header trophy block. */}
        </div>
        {badges.length === 0 ? (
          <div className={styles.emptyCard}>
            <EmptyStateTrophy />
            <p className={styles.emptyCardTitle}>{t('emptyBadgesTitle')}</p>
            <p className={styles.emptyCardDesc}>
              {t('emptyBadgesDesc')}
            </p>
          </div>
        ) : (
          <div className={styles.badgeGrid}>
            {badges.map((b) => (
              <BadgeCard key={b.code} badge={b} />
            ))}
          </div>
        )}
      </section>

      {/* Reward history — cursor-paginated via
          useGamificationHistory + /gamification/history
          endpoint. Initial-display sliced to 5 client-side;
          Load More reveals 5 more per tap, transparently
          fetching the next 50-row page when local pool runs
          out. */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('historyTitle')}</h2>
        {isLoadingFirstPage ? (
          <div className={styles.historyLoading}>
            <Loader2 size={16} className={styles.historyLoadingSpin} />
            {t('loadingHistory')}
          </div>
        ) : entries.length === 0 ? (
          <div className={styles.emptyCard}>
            <EmptyStateTrophy />
            <p className={styles.emptyCardTitle}>{t('emptyHistoryTitle')}</p>
            <p className={styles.emptyCardDesc}>
              {t('emptyHistoryDesc')}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.historyList}>
              {displayedEntries.map((entry) => (
                <HistoryRow key={entry.id} entry={entry} />
              ))}
            </div>
            {/* "Showing X of Y+" hint — Y is the locally-loaded
                pool; the trailing + signals more may exist on
                the server. Subtle; don't compete with the Load
                More button below. */}
            <p className={styles.historyShowingNote}>
              {t('showing', {
                shown: displayedEntries.length,
                total: entries.length,
                more: canFetchMore ? '+' : '',
              })}
            </p>
            {showLoadMore && (
              <button
                type="button"
                className={styles.historyLoadMoreBtn}
                onClick={() => void handleLoadMore()}
                disabled={historyQ.isFetchingNextPage}
              >
                {historyQ.isFetchingNextPage
                  ? <><Loader2 size={12} className={styles.historyLoadingSpin} /> {t('loading')}</>
                  : t('loadMore')}
              </button>
            )}
            {/* Inline error after a failed fetchNextPage. Stays
                visible until the next successful fetch — user
                can retry by tapping Load More again. */}
            {loadMoreError && (
              <p className={styles.historyErrorNote}>
                {t('loadMoreError')}
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
